<template>
  <transition name="toast-fade">
    <div v-if="visible" class="custom-toast" :class="[type, position]">
      <div class="toast-content">
        <div class="toast-icon">
          <i :class="iconClass"></i>
        </div>
        <div class="toast-body">
          <div v-if="title" class="toast-title">{{ title }}</div>
          <div class="toast-message">{{ message }}</div>
        </div>
        <div v-if="showClose" class="toast-close" @click="close">
          <i class="el-icon-close"></i>
        </div>
      </div>
      <div v-if="showProgress && duration > 0" class="toast-progress">
        <div class="toast-progress-bar" :style="{ animationDuration: duration + 'ms' }"></div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'CustomToast',
  data () {
    return {
      visible: false,
      type: 'info',
      title: '',
      message: '',
      duration: 3000,
      showClose: true,
      showProgress: true,
      position: 'top-right',
      timer: null
    }
  },
  computed: {
    iconClass () {
      const icons = {
        success: 'el-icon-circle-check',
        error: 'el-icon-circle-close',
        warning: 'el-icon-warning',
        info: 'el-icon-info'
      }
      return icons[this.type] || icons.info
    }
  },
  methods: {
    show (options = {}) {
      this.type = options.type || 'info'
      this.title = options.title || ''
      this.message = options.message || ''
      this.duration = options.duration !== undefined ? options.duration : 3000
      this.showClose = options.showClose !== undefined ? options.showClose : true
      this.showProgress = options.showProgress !== undefined ? options.showProgress : true
      this.position = options.position || 'top-right'

      this.visible = true

      if (this.timer) {
        clearTimeout(this.timer)
      }

      if (this.duration > 0) {
        this.timer = setTimeout(() => {
          this.close()
        }, this.duration)
      }
    },
    close () {
      this.visible = false
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
    }
  },
  beforeDestroy () {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  }
}
</script>

<style scoped>
.custom-toast {
  position: fixed;
  z-index: 9999;
  min-width: 300px;
  max-width: 420px;
  background: rgba(30, 35, 40, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 1px rgba(255,255,255,0.1) inset;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.08);
}

.custom-toast.top-right {
  top: 24px;
  right: 24px;
}
.custom-toast.top-center {
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
}
.custom-toast.bottom-right {
  bottom: 24px;
  right: 24px;
}

.toast-content {
  display: flex;
  align-items: flex-start;
  padding: 16px 18px;
  gap: 12px;
}

.toast-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.toast-icon i {
  font-size: 20px;
}

.custom-toast.success .toast-icon {
  color: #10b981;
}
.custom-toast.error .toast-icon {
  color: #ef4444;
}
.custom-toast.warning .toast-icon {
  color: #f59e0b;
}
.custom-toast.info .toast-icon {
  color: #25c4c9;
}

.toast-body {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
  line-height: 1.4;
}

.toast-message {
  font-size: 14px;
  color: rgba(255,255,255,0.75);
  line-height: 1.5;
  word-break: break-word;
}

.toast-close {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255,255,255,0.4);
  border-radius: 4px;
  transition: all 0.2s;
}
.toast-close:hover {
  color: rgba(255,255,255,0.8);
  background: rgba(255,255,255,0.1);
}

.toast-progress {
  height: 3px;
  background: rgba(255,255,255,0.1);
}

.toast-progress-bar {
  height: 100%;
  width: 100%;
  transform: scaleX(1);
  transform-origin: left center;
  animation-name: progress-shrink;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
.custom-toast.success .toast-progress-bar {
  background: linear-gradient(90deg, #10b981, #059669);
  transform-origin: right center;
}
.custom-toast.error .toast-progress-bar {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}
.custom-toast.warning .toast-progress-bar {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}
.custom-toast.info .toast-progress-bar {
  background: linear-gradient(90deg, #25c4c9, #12686b);
}

@keyframes progress-shrink {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

.toast-fade-enter-active {
  animation: toast-in 0.3s ease-out;
}
.toast-fade-leave-active {
  animation: toast-out 0.25s ease-in forwards;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
@keyframes toast-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}

.custom-toast.top-center.toast-fade-enter-active {
  animation: toast-in-center 0.3s ease-out;
}
.custom-toast.top-center.toast-fade-leave-active {
  animation: toast-out-center 0.25s ease-in forwards;
}

@keyframes toast-in-center {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
@keyframes toast-out-center {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
}
</style>
